/**
 * Thread Deletion Queue System
 * Prevents threads from reappearing after rapid multiple deletions
 */

import { SupabaseThreadUtils } from "@/lib/db/utils/supabase/threads";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import useAuthStore from "@/store/authStore";
import { useThreadStore } from "@/store/threadStore";

class ThreadDeletionQueue {
  constructor() {
    this.queue = new Map(); // threadId -> deletion operation
    this.processing = new Set(); // threadIds currently being processed
    this.deleted = new Set(); // threadIds that have been successfully deleted
    this.isProcessing = false;
    this.retryAttempts = new Map(); // threadId -> retry count
    this.maxRetries = 3;
    this.batchSize = 5; // Process 5 deletions at once
    this.processingDelay = 100; // Delay between batches
  }

  /**
   * Add a thread deletion to the queue
   * @param {string} threadId - Thread ID to delete
   * @param {Object} options - Deletion options
   * @returns {Promise<boolean>} - Success status
   */
  async enqueueThreadDeletion(threadId, options = {}) {
    if (!threadId) {
      console.error("Thread ID is required for deletion");
      return false;
    }

    // If already deleted, skip
    if (this.deleted.has(threadId)) {
      console.log(`Thread ${threadId} already deleted, skipping`);
      return true;
    }

    // If already in queue, update options
    if (this.queue.has(threadId)) {
      const existing = this.queue.get(threadId);
      this.queue.set(threadId, { ...existing, ...options });
      console.log(`Updated deletion options for thread ${threadId}`);
      return true;
    }

    const deletionOperation = {
      threadId,
      timestamp: Date.now(),
      status: "queued",
      ...options,
    };

    this.queue.set(threadId, deletionOperation);
    console.log(`Queued thread ${threadId} for deletion`);

    // Immediately apply optimistic UI updates
    this.applyOptimisticUpdates(threadId, options);

    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }

    return true;
  }

  /**
   * Apply optimistic UI updates immediately
   * @param {string} threadId - Thread ID
   * @param {Object} options - Deletion options
   */
  applyOptimisticUpdates(threadId, options = {}) {
    const {
      isCurrentThread = false,
      workspaceId = null,
      navigate = null,
      setWorkspaceThreads = null,
      setWorkspaces = null,
    } = options;

    try {
      // Update thread store
      useThreadStore.setState((state) => {
        const updatedState = {
          threads: state.threads.filter((t) => t.id !== threadId),
        };

        // Clear current thread if it's the one being deleted
        if (isCurrentThread || state.currentThreadId === threadId) {
          updatedState.currentThread = null;
          updatedState.currentThreadId = null;
          updatedState.currentThreadTitle = null;
          updatedState.messages = [];

          // Navigate if function provided
          if (navigate) {
            setTimeout(() => navigate("/chat/new"), 50);
          }
        }

        return updatedState;
      });

      // Update workspace threads if workspaceId provided
      if (workspaceId && setWorkspaceThreads) {
        setWorkspaceThreads((prev) => ({
          ...prev,
          [workspaceId]: (prev[workspaceId] || []).filter(
            (t) => t.id !== threadId
          ),
        }));
      }

      // Update global workspaces
      if (setWorkspaces) {
        const { workspaces } = useAuthStore.getState();
        const updatedWorkspaces = workspaces.map((workspace) => {
          if (
            workspace.workspace_threads &&
            workspace.workspace_threads.includes(threadId)
          ) {
            return {
              ...workspace,
              workspace_threads: workspace.workspace_threads.filter(
                (id) => id !== threadId
              ),
            };
          }
          return workspace;
        });
        setWorkspaces(updatedWorkspaces);
      }

      console.log(`Applied optimistic updates for thread ${threadId}`);
    } catch (error) {
      console.error(
        `Error applying optimistic updates for thread ${threadId}:`,
        error
      );
    }
  }

  /**
   * Process the deletion queue
   */
  async processQueue() {
    if (this.isProcessing) return;

    this.isProcessing = true;
    console.log(
      `Starting deletion queue processing with ${this.queue.size} items`
    );

    try {
      while (this.queue.size > 0) {
        // Get next batch
        const batch = Array.from(this.queue.entries())
          .filter(([threadId]) => !this.processing.has(threadId))
          .slice(0, this.batchSize);

        if (batch.length === 0) {
          // All remaining items are being processed, wait a bit
          await new Promise((resolve) =>
            setTimeout(resolve, this.processingDelay)
          );
          continue;
        }

        // Process batch in parallel
        const batchPromises = batch.map(([threadId, operation]) =>
          this.processThreadDeletion(threadId, operation)
        );

        await Promise.allSettled(batchPromises);

        // Small delay between batches to prevent overwhelming the database
        if (this.queue.size > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.processingDelay)
          );
        }
      }
    } catch (error) {
      console.error("Error processing deletion queue:", error);
    } finally {
      this.isProcessing = false;
      console.log("Deletion queue processing completed");
    }
  }

  /**
   * Process a single thread deletion
   * @param {string} threadId - Thread ID
   * @param {Object} operation - Deletion operation
   */
  async processThreadDeletion(threadId, operation) {
    if (this.processing.has(threadId) || this.deleted.has(threadId)) {
      return;
    }

    this.processing.add(threadId);

    try {
      console.log(`Processing deletion for thread ${threadId}`);

      // Update operation status
      operation.status = "processing";
      this.queue.set(threadId, operation);

      // Delete vector store if exists
      const vectorStoreId = operation.vectorStoreId;
      if (vectorStoreId) {
        try {
          const { deleteVectorStore } = await import(
            "@/lib/utils/vectorStoreUtils"
          );
          await deleteVectorStore(vectorStoreId);
          console.log(
            `Deleted vector store ${vectorStoreId} for thread ${threadId}`
          );
        } catch (vectorError) {
          console.error(
            `Error deleting vector store for thread ${threadId}:`,
            vectorError
          );
          // Continue with thread deletion even if vector store deletion fails
        }
      }

      // Delete thread from database
      // Note: deleteThread returns { success: true } for PGRST116 errors (non-existent threads)
      // instead of throwing, so we check the return value
      const deleteResult = await SupabaseThreadUtils.deleteThread({
        supabaseThreadId: threadId,
      });

      // Check if deletion was successful (including idempotent case where thread doesn't exist)
      if (deleteResult && deleteResult.success) {
        // Thread was successfully deleted (or didn't exist, which is fine - idempotent)
        if (!deleteResult.threadId) {
          // If thread didn't exist, log it but treat as success
          console.log(
            `Thread ${threadId} doesn't exist, treating as already deleted (idempotent)`
          );
        }

        // Remove from workspace if specified
        if (operation.workspaceId) {
          try {
            await SupabaseWorkspaceUtils.removeThreadFromWorkspace(
              operation.workspaceId,
              threadId
            );
            console.log(
              `Removed thread ${threadId} from workspace ${operation.workspaceId}`
            );
          } catch (workspaceError) {
            console.error(
              `Error removing thread from workspace:`,
              workspaceError
            );
            // Continue even if workspace removal fails
          }
        }

        // Mark as successfully deleted
        this.deleted.add(threadId);
        this.queue.delete(threadId);
        this.retryAttempts.delete(threadId);

        console.log(`Successfully deleted thread ${threadId}`);

        // Call success callback if provided
        if (operation.onSuccess) {
          try {
            operation.onSuccess(threadId);
          } catch (callbackError) {
            console.error("Error in deletion success callback:", callbackError);
          }
        }
        return;
      }

      // If deleteResult doesn't indicate success, treat as error
      throw new Error(
        `Thread deletion failed: ${JSON.stringify(deleteResult)}`
      );
    } catch (error) {
      console.error(`Error deleting thread ${threadId}:`, error);

      // Handle retry logic
      const retryCount = this.retryAttempts.get(threadId) || 0;
      if (retryCount < this.maxRetries) {
        this.retryAttempts.set(threadId, retryCount + 1);
        operation.status = "retry";
        operation.lastError = error.message;
        this.queue.set(threadId, operation);

        console.log(
          `Will retry deletion for thread ${threadId} (attempt ${retryCount + 1}/${this.maxRetries})`
        );

        // Schedule retry with exponential backoff
        setTimeout(
          () => {
            this.processing.delete(threadId);
          },
          Math.pow(2, retryCount) * 1000
        );
      } else {
        // Max retries reached, remove from queue and call error callback
        console.error(`Max retries reached for thread ${threadId}, giving up`);
        this.queue.delete(threadId);
        this.retryAttempts.delete(threadId);

        if (operation.onError) {
          try {
            operation.onError(threadId, error);
          } catch (callbackError) {
            console.error("Error in deletion error callback:", callbackError);
          }
        }

        // Rollback optimistic updates if possible
        this.rollbackOptimisticUpdates(threadId, operation);
      }
    } finally {
      this.processing.delete(threadId);
    }
  }

  /**
   * Rollback optimistic updates if deletion fails permanently
   * @param {string} threadId - Thread ID
   * @param {Object} operation - Deletion operation
   */
  rollbackOptimisticUpdates(threadId, operation) {
    try {
      console.log(`Rolling back optimistic updates for thread ${threadId}`);

      // This is a best-effort rollback - in practice, we might want to
      // refresh the entire thread list from the database instead
      if (operation.originalThread) {
        useThreadStore.setState((state) => ({
          threads: [...state.threads, operation.originalThread],
        }));
      }

      // Show error message to user
      if (operation.showErrorToast) {
        operation.showErrorToast("Failed to delete thread. Please try again.");
      }
    } catch (error) {
      console.error("Error rolling back optimistic updates:", error);
    }
  }

  /**
   * Check if a thread is queued for deletion
   * @param {string} threadId - Thread ID
   * @returns {boolean}
   */
  isThreadQueuedForDeletion(threadId) {
    return this.queue.has(threadId) || this.deleted.has(threadId);
  }

  /**
   * Cancel a queued deletion (if not yet processing)
   * @param {string} threadId - Thread ID
   * @returns {boolean} - Success status
   */
  cancelThreadDeletion(threadId) {
    if (this.processing.has(threadId)) {
      console.log(
        `Cannot cancel deletion for thread ${threadId} - already processing`
      );
      return false;
    }

    if (this.queue.has(threadId)) {
      this.queue.delete(threadId);
      this.retryAttempts.delete(threadId);
      console.log(`Cancelled deletion for thread ${threadId}`);
      return true;
    }

    return false;
  }

  /**
   * Clear all completed deletions from memory (cleanup)
   */
  cleanup() {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes

    // Clean up old deleted entries
    for (const threadId of this.deleted) {
      const operation = this.queue.get(threadId);
      if (!operation || now - operation.timestamp > maxAge) {
        this.deleted.delete(threadId);
      }
    }

    console.log(
      `Cleaned up deletion queue, ${this.deleted.size} items remaining`
    );
  }

  /**
   * Get queue status for debugging
   */
  getStatus() {
    return {
      queued: this.queue.size,
      processing: this.processing.size,
      deleted: this.deleted.size,
      isProcessing: this.isProcessing,
    };
  }
}

// Create singleton instance
const threadDeletionQueue = new ThreadDeletionQueue();

// Cleanup old entries periodically
setInterval(
  () => {
    threadDeletionQueue.cleanup();
  },
  5 * 60 * 1000
); // Every 5 minutes

export default threadDeletionQueue;

/**
 * Helper function to delete a thread using the queue system
 * @param {string} threadId - Thread ID to delete
 * @param {Object} options - Deletion options
 * @returns {Promise<boolean>} - Success status
 */
export const deleteThreadWithQueue = async (threadId, options = {}) => {
  return await threadDeletionQueue.enqueueThreadDeletion(threadId, options);
};

/**
 * Helper function to check if a thread is being deleted
 * @param {string} threadId - Thread ID
 * @returns {boolean}
 */
export const isThreadBeingDeleted = (threadId) => {
  return threadDeletionQueue.isThreadQueuedForDeletion(threadId);
};
